<template>
  <div
    v-if="loading"
    class="installment-list"
  >
    <div class="animation-container">
      <div class="loading-animation">
        <DotLottieVue
          style="height: 150px; width: 150px; padding: 0"
          autoplay
          loop
          src="/assets/loader.lottie"
          :auto-resize-canvas="true"
        />
        <h4>{{ $t("loading") }}</h4>
      </div>
    </div>
  </div>
  <div v-else-if="installments && installments.installments.length > 0">
    <InstallmentsList
      :title="$t('InstallmentsList-Title')"
      :installment-details="installments"
      @select-installment-item="handleSelectInstallment"
    />
  </div>
  <div v-else-if="error">
    <ErrorMessage />
  </div>
  <div v-else>
    <NoPayments />
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, Ref} from "vue";
import InstallmentsList from "@/components/InstallmentsList.vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import {AnalyticEventsNames,} from "@/helpers/google-analytics.helpers";
import {IInstallment, IInstallmentParams} from "@/types/IInstallment";
import NoPayments from "@/components/NoPayments.vue";
import ErrorMessage from "@/components/ErrorMessage.vue";
import {DotLottieVue} from "@lottiefiles/dotlottie-vue";
import authHelper from "@/helpers/authHelper";
import {ISession} from "@/types/ISession";
import {ActionLogTypeConst} from "@/types/IActionLogs";
import {updateCurrencySymbol} from "@/i18n";
import { useInstallments } from "@/services/DataService";

export default defineComponent({
  name: "InstallmentsView",
  components: {
    DotLottieVue,
    NoPayments,
    ErrorMessage,
    InstallmentsList,
  },
  emits: ["select-installment-item"],
  setup() {
    const {t} = useI18n();
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const i18n = useI18n();

    const deviceTag = computed(() => route.params.deviceTag);
    if (deviceTag.value) {
      store.dispatch("payOptions/setDeviceTag", deviceTag.value);
    }
    // Get the parameter "showoptions" from the URL, if it exists
    // otherwise, set it to option_a

    const forceUpdate = computed(() => {
      // Check if the parameter "forceUpdate" is present in the URL
      // If it is, return true, otherwise, return false.
      // This variable is used to force the update of the installments
      return route.query.forceUpdate === "true";
    });
    const showOptions = computed(() => store.getters["payOptions/getPaymentOption"] || route.query.showoptions || "option_a");
    store.dispatch("payOptions/setPaymentOption", showOptions.value);

    let installments = computed(() => store.getters["payOptions/getInstallments"]);
    if (installments.value) {
      if (installments.value?.currencySymbol) {
        let currentLocale = i18n.locale.value as string;
        let numberFormats = i18n.numberFormats.value;
        updateCurrencySymbol(currentLocale, numberFormats, installments.value.country as string);
      }
    } else {
      const params = {
        deviceTag: deviceTag.value,
        paymentOption: showOptions.value
      } as IInstallmentParams
      let { installments, error } = useInstallments(params)
      if (error) {
        store.dispatch("payOptions/setError", error)
      }
      if (installments) {
        store.dispatch("payOptions/setError", false)
        store.dispatch("payOptions/setInstallments", installments)
        let currentLocale = i18n.locale.value as string;
        let numberFormats = i18n.numberFormats.value;
        updateCurrencySymbol(currentLocale, numberFormats, installments.value?.country as string);
      }
    }

    let error = computed(() => store.getters["payOptions/hasError"]);
    const loading = computed(() => !installments.value);

    return {
      t,
      installments,
      router,
      route,
      deviceTag,
      store,
      loading,
      error,
      forceUpdate,
    };
  },
  watch: {
    installments: {
      handler(newValue: Ref<IInstallment> | undefined) {
        if (newValue) {
          const session: ISession = authHelper.getSession() as ISession;
          if (session && session.selectedInstallment && !this.forceUpdate) {
            if (session.selectedInstallment) {
              this.handleSelectInstallment(session.selectedInstallment);
            }
          }
        }
      },
    },
  },
  mounted() {
    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      // Log the event on page view
      const country = this.installments?.country as string || "";
      const deviceTag: string = this.deviceTag ? this.deviceTag as string : this.$route.params.deviceTag as string;
      authHelper.logAction({
        deviceTag: deviceTag,
        country: country,
        action: AnalyticEventsNames.OnSelectInstallmentPage,
        actionType: ActionLogTypeConst.PAGE_VIEW
      }, this.$gtag)
    }
    window.scrollTo(0, 0);
  },
  beforeUnmount() {
    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      // Log the event on page exit
      const country = this.installments?.country as string || "";
      const deviceTag: string = this.deviceTag ? this.deviceTag as string : this.$route.params.deviceTag as string;
      authHelper.logAction({
        deviceTag: deviceTag,
        country: country,
        action: AnalyticEventsNames.OnSelectInstallmentPage_BrowserClose,
        actionType: ActionLogTypeConst.PAGE_VIEW
      }, this.$gtag)
    }
    if (this.installments) {
      this.store.dispatch("payOptions/setPhoneNumber", this.installments.phoneNumber);
    }
  },
  methods: {
    handleSelectInstallment(selectedInstallmentItem) {
      this.$emit("select-installment-item", selectedInstallmentItem);

      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        // Log the event on Installment selection (only on user interaction)
        if (this.forceUpdate) {
          authHelper.logAction({
            deviceTag: this.deviceTag as string,
            country: this.installments?.country as string,
            action: AnalyticEventsNames.OnSelectInstallmentPage_SelectInstallment,
            actionType: ActionLogTypeConst.BUTTON_CLICK
          }, this.$gtag)
        }
      }

      if (this.installments) {
        this.store.dispatch(
          "payOptions/setCountry",
          this.installments.country
        );
        this.store.dispatch("payOptions/setDeviceTag", this.deviceTag);
      }
      this.store.dispatch(
        "payOptions/setSelectedInstallment",
        selectedInstallmentItem
      );

      this.router.push({name: "ProvidersByDeviceTagAndCountry"});
    },
  },
});
</script>

<style scoped>
.installment-list {
  padding: 16px;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.animation-container {
  justify-content: center;
  align-items: center;
  flex: 1;
  display: flex;
}

.animation-container h4 {
  color: var(--Typography-Text-1);
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
  margin: auto;
  margin-bottom: 80px;
}

.loading-animation {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: auto;
}
</style>
